import api from "../api/quickBid";
import {
  calcMsrp,
  belowMsrp,
  isLuxury,
  buildBidPayload,
  offerLimitReached,
  recordOffer,
  OFFER_LIMIT_MESSAGE,
} from "../utils/quickBid";
import {
  activeOptions,
  autoSelectedOptionIds,
  groupOptionsByCategory,
  toggleOptionSelection,
} from "../utils/carOptions";
import { clearQuickBidDraft } from "../utils/quickBidDraft";
import { findBySlug } from "../utils/slug";

const initialState = () => ({
  makes: [],
  models: [],
  trims: [],
  makeId: null,
  seriesId: null,
  carId: null,
  car: null,
  colorId: null,
  optionIds: [],
  targetPrice: "",
  // Luxury (noPrice) brands: percent under MSRP instead of a dollar target.
  discountPercentage: "",
  // No delivery fee is added to offers.
  deliveryFee: 0,
  step: 1,
  loading: false,
  error: null,
});

// Derivations take state alone so they stay directly unit-testable.
const colorsOf = (state) => (state.car?.variants || []).filter((v) => v.typeCategory === "color");
const optionsOf = (state) => activeOptions(state.car?.variants);
const selectedColorOf = (state) => colorsOf(state).find((v) => v._id === state.colorId) || null;
const selectedOptionsOf = (state) =>
  optionsOf(state).filter((v) => state.optionIds.includes(v._id));
const msrpOf = (state) =>
  calcMsrp({
    car: state.car,
    color: selectedColorOf(state),
    options: selectedOptionsOf(state),
    deliveryFee: state.deliveryFee,
  });

export const quickBid = {
  state: initialState,

  mutations: {
    qbSetField(state, { field, value }) {
      state[field] = value;
    },
    // Selection rules come from utils/carOptions so the product page and Quick
    // Bid behave identically: single-select subcategories (Wheels, …) replace
    // their current pick instead of stacking up.
    qbToggleOption(state, optionId) {
      const options = optionsOf(state);
      const option = options.find((o) => o._id === optionId);
      if (!option) return;
      state.optionIds = toggleOptionSelection(state.optionIds, option, options);
    },
    qbReset(state) {
      Object.assign(state, initialState());
    },
  },

  getters: {
    qbColorVariants: (state) => colorsOf(state),
    qbOptionVariants: (state) => optionsOf(state),
    qbSelectedColor: (state) => selectedColorOf(state),
    qbSelectedOptions: (state) => selectedOptionsOf(state),
    qbGroupedOptions: (state) => groupOptionsByCategory(optionsOf(state)),
    qbMsrp: (state) => msrpOf(state),
    qbBelowMsrp: (state) => belowMsrp(msrpOf(state), state.targetPrice),
    qbIsLuxury: (state) => isLuxury(state.car),
  },

  actions: {
    qbSetStep({ commit }, step) {
      commit("qbSetField", { field: "step", value: step });
    },

    qbReset({ commit }) {
      commit("qbReset");
    },

    // Deep link from the browse pages (/shop/:companyId?seriesId=...). Order
    // matters: qbLoadModels clears seriesId/carId and qbLoadTrims clears carId,
    // so each step must finish before the next sets its id.
    async qbPrefill({ dispatch }, { makeId, seriesId, carId }) {
      await dispatch("qbLoadMakes");
      if (makeId) await dispatch("qbLoadModels", makeId);
      if (seriesId) await dispatch("qbLoadTrims", seriesId);
      if (carId) await dispatch("qbSelectCar", carId);
    },

    // Deep link by URL slugs (/quick-bid/audi/a5/all-new-a5). Each level is
    // resolved against the catalog just loaded; an unknown segment stops there
    // and leaves the rest for the user to pick.
    async qbPrefillBySlugs({ dispatch, state }, { make, model, trim }) {
      await dispatch("qbLoadMakes");
      const makeItem = findBySlug(state.makes, make);
      if (!makeItem) return { matched: "none" };
      await dispatch("qbLoadModels", makeItem._id);
      const modelItem = findBySlug(state.models, model, { makeName: makeItem.name, kind: "model" });
      if (!modelItem) return { matched: "make" };
      await dispatch("qbLoadTrims", modelItem._id);
      const trimItem = findBySlug(state.trims, trim);
      if (!trimItem) return { matched: "model" };
      await dispatch("qbSelectCar", trimItem._id);
      return { matched: "trim" };
    },

    async qbLoadMakes({ commit }) {
      commit("qbSetField", { field: "error", value: null });
      commit("qbSetField", { field: "loading", value: true });
      try {
        commit("qbSetField", { field: "makes", value: await api.fetchMakes() });
      } catch (e) {
        commit("qbSetField", { field: "error", value: "Could not load makes. Please try again." });
      } finally {
        commit("qbSetField", { field: "loading", value: false });
      }
    },

    async qbLoadModels({ commit }, companyId) {
      commit("qbSetField", { field: "error", value: null });
      commit("qbSetField", { field: "makeId", value: companyId });
      commit("qbSetField", { field: "seriesId", value: null });
      commit("qbSetField", { field: "carId", value: null });
      commit("qbSetField", { field: "car", value: null });
      commit("qbSetField", { field: "models", value: [] });
      commit("qbSetField", { field: "trims", value: [] });
      if (!companyId) return;
      commit("qbSetField", { field: "loading", value: true });
      try {
        commit("qbSetField", { field: "models", value: await api.fetchModels(companyId) });
      } catch (e) {
        commit("qbSetField", { field: "error", value: "Could not load models. Please try again." });
      } finally {
        commit("qbSetField", { field: "loading", value: false });
      }
    },

    async qbLoadTrims({ commit, state }, seriesId) {
      commit("qbSetField", { field: "error", value: null });
      commit("qbSetField", { field: "seriesId", value: seriesId });
      commit("qbSetField", { field: "carId", value: null });
      commit("qbSetField", { field: "car", value: null });
      commit("qbSetField", { field: "trims", value: [] });
      if (!seriesId || !state.makeId) return;
      commit("qbSetField", { field: "loading", value: true });
      try {
        commit("qbSetField", {
          field: "trims",
          value: await api.fetchTrims(state.makeId, seriesId),
        });
      } catch (e) {
        commit("qbSetField", { field: "error", value: "Could not load trims. Please try again." });
      } finally {
        commit("qbSetField", { field: "loading", value: false });
      }
    },

    async qbSelectCar({ commit }, carId) {
      commit("qbSetField", { field: "error", value: null });
      commit("qbSetField", { field: "carId", value: carId });
      commit("qbSetField", { field: "car", value: null });
      commit("qbSetField", { field: "colorId", value: null });
      commit("qbSetField", { field: "optionIds", value: [] });
      if (!carId) return;
      commit("qbSetField", { field: "loading", value: true });
      try {
        const car = await api.fetchCar(carId);
        commit("qbSetField", { field: "car", value: car });
        // Preselect the first colour that has a photo, matching the existing
        // configurator (Product.vue:500-509). Colours without a photo are not
        // shown, so they must not be preselected either.
        const firstColor = (car?.variants || []).find(
          (v) => v.typeCategory === "color" && v.isActive && v.url && v.url.length > 0
        );
        if (firstColor) commit("qbSetField", { field: "colorId", value: firstColor._id });
        // Options that come with the car start selected, as on the product page
        // (FormQuery.vue autoSelectIncludedOptions). They are free, so this
        // does not move the MSRP.
        commit("qbSetField", {
          field: "optionIds",
          value: autoSelectedOptionIds(activeOptions(car?.variants)),
        });
      } catch (e) {
        commit("qbSetField", {
          field: "error",
          value: "Could not load this trim. Please try again.",
        });
      } finally {
        commit("qbSetField", { field: "loading", value: false });
      }
    },

    async qbSubmit({ commit, state, getters, rootGetters }) {
      commit("qbSetField", { field: "error", value: null });
      // UX-02: 2 offers per 24h. Say so up front; the server enforces it too.
      const userId = rootGetters.user?._id;
      if (offerLimitReached(userId)) {
        commit("qbSetField", { field: "error", value: OFFER_LIMIT_MESSAGE });
        return false;
      }
      commit("qbSetField", { field: "loading", value: true });
      try {
        const payload = buildBidPayload({
          car: state.car,
          color: getters.qbSelectedColor,
          options: getters.qbSelectedOptions,
          targetPrice: state.targetPrice,
          user: rootGetters.user,
          deliveryFee: state.deliveryFee,
          comments: "",
          discountPercentage: state.discountPercentage,
        });
        await api.submitQuickBid(payload);
        recordOffer(userId);
        commit("qbSetField", { field: "step", value: 4 });
        clearQuickBidDraft();
        return true;
      } catch (e) {
        const serverMessage = e?.response?.data?.data?.message;
        // 429 (or a message that names the limit) -> the friendly limit text.
        const limited = e?.response?.status === 429 || /24 ?h|24 hours|limit/i.test(serverMessage || "");
        commit("qbSetField", {
          field: "error",
          value: limited
            ? OFFER_LIMIT_MESSAGE
            : serverMessage || "Could not send your offer. Please try again.",
        });
        return false;
      } finally {
        commit("qbSetField", { field: "loading", value: false });
      }
    },
  },
};

export default quickBid;
